/**
 * Utility functions for barcode grade colors and categorization
 */

export const LIGHT_GREEN = '#2ED2AB';
export const LIGHT_ORANGE = '#FF9800';
export const GV_RED = '#EA3632';

// Define the grade order for comparison
export const gradeOrder: { [key: string]: number } = {
  A: 5,
  B: 4,
  C: 3,
  D: 2,
  F: 1,
};

// Valid passing status values
const VALID_PASSING_STATUSES = ['pass', 'warning', 'fail'] as const;
export type PassingStatusCategory = (typeof VALID_PASSING_STATUSES)[number];
export const validateAndNormalizePassingStatus = (passingStatus?: string): PassingStatusCategory | null => {
  if (!passingStatus) {
    return null;
  }

  const normalizedStatus = passingStatus.toLowerCase();
  if (VALID_PASSING_STATUSES.includes(normalizedStatus as PassingStatusCategory)) {
    return normalizedStatus as PassingStatusCategory;
  }

  return null;
};

// Helper function to determine grade category based on passingStatus
export const getGradeCategory = (grade: string | undefined, passingStatus?: string): 'pass' | 'warning' | 'fail' => {
  // If passingStatus is provided, validate and use it
  const validatedStatus = validateAndNormalizePassingStatus(passingStatus);
  if (validatedStatus) {
    return validatedStatus;
  }

  // Fallback: Per product requirements: 'no grade means no issue'.
  // If grade is invalid or undefined, return 'pass' to indicate no issue.
  if (!grade || !(grade in gradeOrder)) {
    return 'pass';
  }

  // Fallback: Default behavior when passingStatus is not available: A or B = pass
  return grade === 'A' || grade === 'B' ? 'pass' : grade === 'C' || grade === 'D' ? 'warning' : 'fail';
};

// Helper function to get color based on category
export const getCategoryColor = (category: 'pass' | 'warning' | 'fail'): string => {
  switch (category) {
    case 'pass':
      return LIGHT_GREEN;
    case 'warning':
      return LIGHT_ORANGE;
    case 'fail':
      return GV_RED;
    default:
      // This should never happen due to TypeScript type checking, but provides runtime safety
      console.warn(`Unexpected category value: ${category}.`);
      return GV_RED;
  }
};

export const getBarcodeGradeColor = (grade: string | undefined, passingStatus?: string): string => {
  const category = getGradeCategory(grade, passingStatus);
  return getCategoryColor(category);
};
